// components/ProfileDropdown.jsx
import React, { useState, useRef, useEffect } from 'react';
import '../../styles/Auth/profileDropdown.css';
import ResetPasswordPage from '../../pages/Auth/Reset-Password/reset-password.jsx';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getData, postData } from "../../../src/webService/webservice.js";
import { useSubscriptionRenewal } from '../../context/SubscriptionRenewalContext.jsx';
import { Icon } from "@iconify/react";
import { getNavItems } from "./navConfig.jsx";
import TVFMLoader from '../../pages/Auth/Loader/loader.jsx';
import { useStore } from "../../Context/context.jsx";
import { Typography } from '@mui/material';
import Swal from 'sweetalert2';
import { clearStorageKeepEod } from '../../utils/utils.js';
import NotificationIcon from './NotificationIcon.jsx';

const formatRoleName = (roleStr) => {
    if (!roleStr) return "";
    const roleMapping = {
        SuperAdmin: "Super Admin",
        SelfAdmin: "Self Admin",
        FranchiseAdmin: "Franchise Admin",
        ClusterManager: "Cluster Manager",
        StoreAdmin: "Store Admin",
        StoreManager: "Store Manager",
        Accountant: "Accountant",
        Biller: "Biller"
    };
    return roleMapping[roleStr] || roleStr.replace(/([A-Z])/g, ' $1').trim();
};

const ProfileDropdown = () => {
    const role = localStorage.getItem("role");
    const adminStoreIds = JSON.parse(localStorage.getItem("storeIds") || "[]");
    const initialStoreId =
        localStorage.getItem("currentStoreId") ||
        adminStoreIds[0] ||
        "";
    const userId = localStorage.getItem("userId");
    const [clusterStoreIds, setClusterStoreIds] = useState([]);
    const [stores, setStores] = useState([]);
    const [PreviewUrl, setPreviewUrl] = useState("");
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [storeSearch, setStoreSearch] = useState('');
    const [storeDropdownOpen, setStoreDropdownOpen] = useState(false);
    const storeDropdownRef = useRef();
    const navigate = useNavigate();
    const location = useLocation();
    const dropdownRef = useRef();
    const [open, setOpen] = useState(false);
    const [showResetModal, setShowResetModal] = useState(false);
    const [screenLoading, setScreenLoading] = useState(false);
    const homeRoute = role === "Biller" ? "/appointments" : "/dashboard";
    const isHomePage = location.pathname === homeRoute;
    const isPosPage = location.pathname === "/pos";
    const { selectedStoreId, setSelectedStoreId } = useStore();
    const hideOnPages =
        location.pathname.includes("/stores") ||
        location.pathname.includes("/franchisor");

    const { subscriptionData, openRenewPackageModal } = useSubscriptionRenewal();

    const getTickerMessage = (sub) => {
        if (!sub) return "";
        const pkgName = sub.packageName || "N/A";
        const expDateStr = sub.finalExpiryDate 
            ? new Date(sub.finalExpiryDate).toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric' })
            : "N/A";
        const remDays = sub.remainingDays < 0 ? 0 : sub.remainingDays;

        if (sub.subscriptionStatus === 'Expired' || sub.remainingDays < 0) {
            return `🚨 Your subscription has expired • Package: ${pkgName} • Expiry Date: ${expDateStr} • Please renew your package immediately to continue using the POS.`;
        } else if (remDays === 0) {
            return `🚨 Your subscription expires today • Package: ${pkgName} • Expiry Date: ${expDateStr} • Please renew immediately to avoid service interruption.`;
        } else if (remDays <= 7) {
            return `⚠️ Your subscription will expire in ${remDays} days • Package: ${pkgName} • Expiry Date: ${expDateStr} • Please renew your subscription soon.`;
        } else {
            return `⚠️ Your subscription will expire in ${remDays} days • Package: ${pkgName} • Expiry Date: ${expDateStr} • Please renew your subscription to continue uninterrupted service.`;
        }
    };

    const handleScreenNavigate = (route) => {
        if (location.pathname === route) return;

        setScreenLoading(true);
        navigate(route);
    };


    useEffect(() => {
        const storedStoreId = localStorage.getItem("currentStoreId");

        if (storedStoreId && storedStoreId !== selectedStoreId) {
            setSelectedStoreId(storedStoreId);
        }
    }, []);





    const [formData, setFormData] = useState(
        {
            name: "",
            email: "",
            phoneNumber: "",
            employeeImageObject: {},
        }
    );

    const [currentTime, setCurrentTime] = useState(new Date());

    const confirmLogout = () => {
        clearStorageKeepEod();
        toast.success('Logout successful!');
        navigate('/login');
    };

    const handleLogoutClick = async () => {
        const token = localStorage.getItem("Token");
        const ADMIN_ROLES = ["SuperAdmin", "SelfAdmin", "Accountant", "Manager", "StoreCreator"];
        if (ADMIN_ROLES.includes(role) || !selectedStoreId) {
            setShowLogoutModal(true);
            return;
        }

        try {
            const res = await getData(
                "eod/opening-cash-status",
                {
                    storeId: selectedStoreId
                },
                token
            );

            if (res?.data?.isTodayClosed === true) {
                setShowLogoutModal(true);
            } else {
                const result = await Swal.fire({
                    icon: "warning",
                    title: "EOD Closing Pending",
                    text: "You have not completed the End of Day (EOD) Closing for today. Please complete the EOD Closing before logging out.",
                    showCancelButton: true,
                    showDenyButton: true,
                    confirmButtonText: "Go to EOD Report",
                    denyButtonText: "Logout Anyway",
                    cancelButtonText: "Cancel",
                    confirmButtonColor: "#3C0008",
                    denyButtonColor: "#64748b",
                    cancelButtonColor: "#d33",
                    allowOutsideClick: false
                });

                if (result.isConfirmed) {
                    navigate("/reports/eod");
                } else if (result.isDenied) {
                    confirmLogout();
                }
            }
        } catch (err) {
            console.error("Error checking EOD status on logout:", err);
            setShowLogoutModal(true);
        }
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpen(false);
            }
            if (storeDropdownRef.current && !storeDropdownRef.current.contains(event.target)) {
                setStoreDropdownOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const token = localStorage.getItem("Token");
                const userId = localStorage.getItem("userId");
                const response = await getData(`users/user/${userId}`, token);
                const user = response?.data;
                setFormData({
                    name: user?.name || "",
                    email: user?.email || "",
                    phoneNumber: user?.phoneNumber || "",
                    employeeImageObject: user?.employeeImageObject || {},
                });
                if (user.employeeImageObject?.uri) {
                    setPreviewUrl(`${process.env.REACT_APP_API_URL}${user.employeeImageObject.uri}`);
                } else {
                    setPreviewUrl("/mdi--user.svg");
                }
            } catch (err) {
                console.error("Failed to fetch user details:", err);
            }
        };

        fetchUser();
    }, []);

    useEffect(() => {
        const fetchStores = async () => {
            try {
                const token = localStorage.getItem("Token");
                const data = await getData("stores/stores", { limit: 1000 }, token);
                console.log("API Full Response:", data);

                const results = data?.data?.results || [];
                setStores(results);
            } catch (error) {
                console.error("Failed to load stores", error);
            }
        };
        fetchStores();
    }, []);

    const handleStoreChange = (e) => {
        const storeId = e.target.value;
        setSelectedStoreId(storeId);
        localStorage.setItem("currentStoreId", storeId); // ✅ ADD THIS
    };





    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);



    const navItems = getNavItems(role);

    const getCurrentNavItem = () => {
        const currentPath = location.pathname.toLowerCase();

        for (const item of navItems) {
            // 1️⃣ Check subItems FIRST
            if (item.subItems) {
                const sub = item.subItems.find((s) =>
                    currentPath.startsWith(s.path.toLowerCase())
                );
                if (sub) return sub;
            }

            // 2️⃣ Check parent menu AFTER submenus
            if (item.path && currentPath.startsWith(item.path.toLowerCase())) {
                return item;
            }
        }

        return { label: "Home", icon: <Icon icon="mdi:home" /> };
    };


    const currentPage = getCurrentNavItem();

    useEffect(() => {
        setScreenLoading(false);
    }, [location.pathname]);




    return (
        <>
            <div className='header-container'>

                <div className="header-left">
                    <h2 className="page-title">
                        <Typography variant="subtitle1" color='white' sx={{ fontWeight: "bold" }}>
                            Welcome Back,
                        </Typography>
                        <span className="admin-label">
                            {formData.name} <span style={{ color: "white", fontWeight: "bold" }}>({formatRoleName(role)})</span>
                        </span>
                        {/* {currentTime.toLocaleDateString()}, {currentTime.toLocaleTimeString()} */}
                    </h2>

                    {!hideOnPages &&
                        !['SuperAdmin', 'SelfAdmin', 'Manager', 'Accountant'].includes(role) &&
                        (role === 'FranchiseAdmin' || role === 'ClusterManager' || role === 'StoreAdmin' || role === 'StoreManager' || role === 'Biller') &&
                        adminStoreIds.length > 0 && (() => {
                            const myStores = adminStoreIds
                                .map(id => stores.find(s => s._id === id))
                                .filter(Boolean);

                            const selectedStore = myStores.find(s => s._id === selectedStoreId);
                            const filtered = myStores.filter(s =>
                                s.storeName?.toLowerCase().includes(storeSearch.toLowerCase())
                            );

                            // Single store — just show a label
                            if (myStores.length === 1) {
                                return (
                                    <span style={{
                                        fontSize: '13px', fontWeight: 600,
                                        color: '#fff', padding: '6px 12px',
                                        background: 'rgba(255,255,255,0.1)',
                                        borderRadius: '8px'
                                    }}>
                                        {myStores[0]?.storeName || 'Store'}
                                    </span>
                                );
                            }

                            // Multiple stores — searchable custom dropdown
                            return (
                                <div ref={storeDropdownRef} style={{ position: 'relative' }}>
                                    {/* Trigger Button */}
                                    <div
                                        onClick={() => { setStoreDropdownOpen(prev => !prev); setStoreSearch(''); }}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '8px',
                                            padding: '6px 12px', borderRadius: '8px',
                                            background: 'rgba(255,255,255,0.12)',
                                            border: '1px solid rgba(255,255,255,0.25)',
                                            cursor: 'pointer', color: '#fff',
                                            fontSize: '13px', fontWeight: 600,
                                            minWidth: '160px', userSelect: 'none'
                                        }}
                                    >
                                        <Icon icon="mdi:store-outline" style={{ fontSize: '16px' }} />
                                        <span style={{ flex: 1 }}>{selectedStore?.storeName || 'Select Store'}</span>
                                        <Icon icon={storeDropdownOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'} style={{ fontSize: '16px' }} />
                                    </div>

                                    {/* Dropdown Panel */}
                                    {storeDropdownOpen && (
                                        <div style={{
                                            position: 'absolute', top: 'calc(100% + 6px)', left: 0,
                                            minWidth: '220px', zIndex: 9999,
                                            background: '#fff', borderRadius: '10px',
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                                            overflow: 'hidden'
                                        }}>
                                            {/* Search Input */}
                                            <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <Icon icon="mdi:magnify" style={{ fontSize: '16px', color: '#888' }} />
                                                <input
                                                    autoFocus
                                                    type="text"
                                                    placeholder="Search store..."
                                                    value={storeSearch}
                                                    onChange={e => setStoreSearch(e.target.value)}
                                                    onClick={e => e.stopPropagation()}
                                                    style={{
                                                        border: 'none', outline: 'none',
                                                        fontSize: '12px', width: '100%',
                                                        background: 'transparent', color: '#333'
                                                    }}
                                                />
                                            </div>
                                            {/* Store List */}
                                            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                {filtered.length > 0 ? filtered.map(store => (
                                                    <div
                                                        key={store._id}
                                                        onClick={() => {
                                                            setSelectedStoreId(store._id);
                                                            localStorage.setItem('currentStoreId', store._id);
                                                            setStoreDropdownOpen(false);
                                                            setStoreSearch('');
                                                        }}
                                                        style={{
                                                            padding: '10px 14px', fontSize: '13px', cursor: 'pointer',
                                                            color: store._id === selectedStoreId ? '#3C0008' : '#333',
                                                            fontWeight: store._id === selectedStoreId ? 700 : 400,
                                                            background: store._id === selectedStoreId ? '#FDF0F2' : 'transparent',
                                                            display: 'flex', alignItems: 'center', gap: '8px',
                                                            transition: 'background 0.15s'
                                                        }}
                                                        onMouseEnter={e => e.currentTarget.style.background = store._id === selectedStoreId ? '#FDF0F2' : '#f7f7f7'}
                                                        onMouseLeave={e => e.currentTarget.style.background = store._id === selectedStoreId ? '#FDF0F2' : 'transparent'}
                                                    >
                                                        <Icon icon={store._id === selectedStoreId ? 'mdi:check-circle' : 'mdi:store-outline'} style={{ fontSize: '15px', color: store._id === selectedStoreId ? '#3C0008' : '#aaa' }} />
                                                        {store.storeName}
                                                    </div>
                                                )) : (
                                                    <div style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: '#aaa' }}>No stores found</div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}



                </div>

                {(() => {
                    let calculatedRemainingDays = undefined;
                    if (subscriptionData?.finalExpiryDate) {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const expiry = new Date(subscriptionData.finalExpiryDate);
                        expiry.setHours(0, 0, 0, 0);
                        calculatedRemainingDays = Math.round((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                    } else if (subscriptionData?.remainingDays !== undefined) {
                        calculatedRemainingDays = subscriptionData.remainingDays;
                    }
                    
                    // Inject calculatedRemainingDays into subscriptionData so getTickerMessage works correctly
                    const effectiveSubData = { ...subscriptionData, remainingDays: calculatedRemainingDays };

                    const shouldShowSubscriptionAlert =
                        role === "StoreAdmin" &&
                        effectiveSubData &&
                        effectiveSubData.finalExpiryDate &&
                        effectiveSubData.remainingDays <= 15 &&
                        effectiveSubData.remainingDays >= 0;
                        
                    const showExpiredAlert = 
                        role === "StoreAdmin" && 
                        effectiveSubData && 
                        (effectiveSubData.subscriptionStatus === 'Expired' || effectiveSubData.remainingDays < 0);

                    if (!shouldShowSubscriptionAlert && !showExpiredAlert) {
                        return null;
                    }

                    return (
                    <div 
                        className={`header-ticker-wrapper ${
                            effectiveSubData.subscriptionStatus === 'Expired' || effectiveSubData.remainingDays < 0
                                ? 'sub-ticker-expired'
                                : effectiveSubData.remainingDays === 0
                                    ? 'sub-ticker-critical'
                                    : effectiveSubData.remainingDays <= 7
                                        ? 'sub-ticker-high'
                                        : 'sub-ticker-warning'
                        }`}
                        title="Click to Renew Subscription"
                        onClick={() => {
                            openRenewPackageModal();
                        }}
                    >
                        <div className="header-ticker-track">
                            <span className="header-ticker-item">{getTickerMessage(effectiveSubData)}</span>
                            <span className="header-ticker-item">{getTickerMessage(effectiveSubData)}</span>
                        </div>
                        <button className="header-ticker-renew-badge">Renew</button>
                    </div>
                    );
                })()}

                <div className="header-right" ref={dropdownRef}>


                    {!["StoreCreator", "Accountant", "SelfAdmin", "FranchiseAdmin", "SuperAdmin"].includes(role) && (
                        <>
                            <button
                                className="header-btn active"
                                disabled
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    cursor: "default",
                                }}
                            >
                                {currentPage.icon}
                                {currentPage.label}
                            </button>
                        </>
                    )}

                    {/* ---- Notifications ---- */}
                    <NotificationIcon />

                    {/* ---- Help & Support ---- */}
                    <a
                        href="tel:9600602063"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '7px',
                            padding: '6px 14px',
                            borderRadius: '20px',
                            background: 'rgba(255,255,255,0.12)',
                            border: '1px solid rgba(255,255,255,0.25)',
                            color: '#fff',
                            textDecoration: 'none',
                            fontSize: '12px',
                            fontWeight: 600,
                            letterSpacing: '0.3px',
                            whiteSpace: 'nowrap',
                            transition: 'background 0.2s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.22)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
                        title="Call Support"
                    >
                        <span style={{
                            width: '7px', height: '7px', borderRadius: '50%',
                            background: '#4ade80',
                            boxShadow: '0 0 6px #4ade80',
                            flexShrink: 0
                        }} />
                        <Icon icon="mdi:headset" style={{ fontSize: '16px' }} />
                        <span className="help-text">Help &amp; Support: 9600602063</span>
                    </a>

                    <div className="profile-section" onClick={() => setOpen(prev => !prev)}>
                        <img src={PreviewUrl} alt="Profile" className="profile-icon" />
                    </div>

                    {open && (
                        <div className="dropdown-menu">
                            {role === "StoreAdmin" && subscriptionData && (
                                <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', background: '#fafafa', fontSize: '13px' }}>
                                    <div style={{ marginBottom: '6px' }}>
                                        <strong style={{ color: '#3C0008' }}>Package:</strong> <span style={{ color: '#555' }}>{subscriptionData.packageName || 'N/A'}</span>
                                    </div>
                                    <div>
                                        <strong style={{ color: '#3C0008' }}>Valid Up To:</strong> <span style={{ color: '#555' }}>{subscriptionData.finalExpiryDate ? new Date(subscriptionData.finalExpiryDate).toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}</span>
                                    </div>
                                </div>
                            )}
                            <div className="user-info">

                                {role !== "StoreCreator" && (
                                    <>
                                        <div
                                            className="dropdown-item"
                                            onClick={() => navigate('/settings/profile')}
                                        >
                                            <strong>Profile</strong>
                                        </div>

                                        <div
                                            className="dropdown-item"
                                            onClick={() => navigate('/settings/profile')}
                                        >
                                            <strong>Change Password</strong>
                                        </div>
                                    </>
                                )}

                                <div
                                    className="dropdown-item"
                                    onClick={handleLogoutClick}
                                >
                                    Logout
                                </div>

                            </div>
                        </div>
                    )}
                </div>

            </div>


            {showLogoutModal && (
                <div className="logout-overlay">
                    <div className="logout-modal">

                        <button
                            className="logout-close"
                            onClick={() => setShowLogoutModal(false)}
                        >
                            &times;
                        </button>

                        <div className="logout-icon-box">
                            <Icon icon="mdi:logout" width="50" height="50" className="logout-icon" />
                        </div>

                        <h2 className="logout-title">Confirm Logout</h2>
                        <p className="logout-text">Are you sure you want to sign out from your account?</p>

                        <div className="logout-actions">
                            <button className="btn-confirm" onClick={confirmLogout}>
                                Yes, Logout
                            </button>
                            <button className="btn-cancel" onClick={() => setShowLogoutModal(false)}>
                                Cancel
                            </button>
                        </div>

                    </div>
                </div>
            )}


            {showResetModal && (
                <ResetPasswordPage onClose={() => setShowResetModal(false)} />
            )}




            {screenLoading && <TVFMLoader />}




        </>
    );

};

export default ProfileDropdown;
